
/**
 * Enhanced performance tracking utility with component rendering metrics
 */

// Only track performance in development
const isDev = process.env.NODE_ENV === 'development';

type PerformanceMark = {
  name: string;
  startTime: number;
};

// Track component render counts to identify excessive re-renders
const renderCounts: Record<string, number> = {};

// Track slow operations to highlight in summary
const slowOperations: Array<{name: string, duration: number, message?: string}> = [];

// Track cumulative time spent on each operation type
const operationTimes: Record<string, {count: number, totalTime: number}> = {};

// Memory tracking if available
const getMemoryInfo = (): {jsHeapSizeLimit?: number, totalJSHeapSize?: number, usedJSHeapSize?: number} => {
  if (isDev && window.performance && (performance as any).memory) {
    return (performance as any).memory;
  }
  return {};
};

export const performance = {
  /**
   * Get current timestamp in milliseconds
   */
  now: () => Date.now(),
  
  /**
   * Start timing an operation
   * @param name Identifier for this operation
   * @returns Performance mark object
   */
  start: (name: string): PerformanceMark => {
    const mark = {
      name,
      startTime: Date.now()
    };
    
    // Only log in development mode
    if (isDev) {
      const category = name.split('-')[0] || 'general';
      console.log(`⏱️ [PERF] Starting: ${name} (category: ${category})`);
      
      // Track component render counts
      if (name.startsWith('render-') || name.includes('-component')) {
        renderCounts[name] = (renderCounts[name] || 0) + 1;
        if (renderCounts[name] > 3) {
          console.warn(`⚠️ [PERF] Component ${name} has rendered ${renderCounts[name]} times!`);
        }
      }
    }
    
    return mark;
  },

  /**
   * End timing an operation and log the result
   * @param mark The performance mark from start()
   * @param message Optional message to include
   * @returns Duration in milliseconds
   */
  end: (mark: PerformanceMark, message?: string): number => {
    const duration = Date.now() - mark.startTime;
    
    // Skip all logging in production
    if (!isDev) return duration;
    
    const category = mark.name.split('-')[0] || 'general';
    
    // Track cumulative time for this operation type
    const operationKey = mark.name.split('-')[0] || 'general';
    if (!operationTimes[operationKey]) {
      operationTimes[operationKey] = { count: 0, totalTime: 0 };
    }
    operationTimes[operationKey].count += 1;
    operationTimes[operationKey].totalTime += duration;
    
    const performanceLog = `⏱️ [PERF] ${mark.name} completed in ${duration}ms${message ? `: ${message}` : ''}`;
    
    // Highlight slow operations - directly log without setTimeout
    if (duration > 500) {
      console.warn(performanceLog + ' ⚠️');
      slowOperations.push({
        name: mark.name,
        duration,
        message
      });
    } else {
      console.log(performanceLog);
    }
    
    return duration;
  },
  
  /**
   * Reset render count tracking
   */
  resetCounts: (): void => {
    Object.keys(renderCounts).forEach(key => {
      delete renderCounts[key];
    });
    
    // Also clear slow operations and operation times
    slowOperations.length = 0;
    Object.keys(operationTimes).forEach(key => {
      delete operationTimes[key];
    });
  },
  
  /**
   * Get current render counts for debugging
   */
  getRenderCounts: (): Record<string, number> => {
    return { ...renderCounts };
  },
  
  /**
   * Get performance summary of slow operations
   */
  getSlowOperations: () => {
    return [...slowOperations].sort((a, b) => b.duration - a.duration);
  },
  
  /**
   * Get cumulative time spent per operation category
   */
  getOperationTimes: () => {
    return { ...operationTimes };
  },
  
  /**
   * Get memory usage if available
   */
  getMemoryUsage: () => {
    return getMemoryInfo();
  },
  
  /**
   * Log a performance summary to console
   */
  logSummary: () => {
    if (!isDev) return null;
    
    const slow = performance.getSlowOperations();
    if (slow.length > 0) {
      console.group('🔍 Performance Issues Summary');
      console.warn(`Found ${slow.length} slow operations (>500ms):`);
      slow.forEach((op, i) => {
        console.warn(`${i+1}. ${op.name}: ${op.duration}ms${op.message ? ` - ${op.message}` : ''}`);
      });
      console.groupEnd();
    }
    
    const reRenders = Object.entries(renderCounts)
      .filter(([_, count]) => count > 3)
      .sort(([_, countA], [__, countB]) => countB - countA);
      
    if (reRenders.length > 0) {
      console.group('🔍 Excessive Re-renders');
      console.warn(`Found ${reRenders.length} components with excessive re-renders:`);
      reRenders.forEach(([name, count], i) => {
        console.warn(`${i+1}. ${name}: ${count} renders`);
      });
      console.groupEnd();
    }
    
    const opTimes = Object.entries(operationTimes)
      .sort(([_, a], [__, b]) => b.totalTime - a.totalTime);
      
    if (opTimes.length > 0) {
      console.group('🔍 Cumulative Operation Times');
      opTimes.forEach(([category, data], i) => {
        console.log(`${i+1}. ${category}: ${data.totalTime}ms total across ${data.count} calls (avg: ${Math.round(data.totalTime/data.count)}ms)`);
      });
      console.groupEnd();
    }
    
    const memInfo = getMemoryInfo();
    if (memInfo.usedJSHeapSize) {
      console.group('🔍 Memory Usage');
      console.log(`Used: ${Math.round(memInfo.usedJSHeapSize / (1024 * 1024))}MB / ${Math.round(memInfo.jsHeapSizeLimit! / (1024 * 1024))}MB`);
      console.groupEnd();
    }
    
    return {
      slowOperations: slow,
      excessiveRenders: reRenders,
      operationTimes: opTimes,
      memoryUsage: memInfo
    };
  }
};
